// Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams, HttpResponse, HttpResponseBase } from '@angular/common/http';

// External Modules
import * as uuid from 'uuid';

// Environment
import { environment } from 'environments/environment';
import { first, tap, map } from 'rxjs/operators';

// Http Context
import { BYPASS, CIP } from 'app/auth/helpers';

// Models
import * as models from 'app/center/models/index';

// RxJS
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class Center {
  // Private
  private currentCIPSubject = new BehaviorSubject<string>(null);
  private currentCenterAdvancedSubject = new BehaviorSubject<models.CenterAdvancedInfo>(null);
  private currentCenterBasicSubject = new BehaviorSubject<models.CenterBasicInfo>(null);

  // Public
  public $currentCIP = this.currentCIPSubject.asObservable();
  public $currentCenterAdvanced = this.currentCenterAdvancedSubject.asObservable();
  public $currentCenterBasic = this.currentCenterBasicSubject.asObservable();

  constructor(private _http: HttpClient) {
    // const currentCenter = JSON.parse(localStorage.getItem('currentCenter'));
    // if (currentCenter !== undefined) {
    //   if (typeof currentCenter?.cip !== 'string') {
    //     return;
    //   }
    //   if (!currentCenter.hasOwnProperty('advanced') || !currentCenter.hasOwnProperty('basic')) {
    //     return;
    //   }
    //   this.currentCIP = currentCenter.cip;
    //   this.currentCenterAdvanced = currentCenter.advanced;
    //   this.currentCenterBasic = currentCenter.basic;
    //   localStorage.removeItem(currentCenter);
    // }
  }

  get currentCIP(): string {
    return this.currentCIPSubject.getValue();
  }
  get currentCenterAdvanced(): models.CenterAdvancedInfo {
    return this.currentCenterAdvancedSubject.getValue();
  }
  get currentCenterBasic(): models.CenterBasicInfo {
    return this.currentCenterBasicSubject.getValue();
  }

  set currentCIP(cip: string) {
    this.currentCIPSubject.next(cip);
  }
  set currentCenterAdvanced(center: models.CenterAdvancedInfo) {
    this.currentCenterAdvancedSubject.next(center);
  }
  set currentCenterBasic(center: models.CenterBasicInfo) {
    this.currentCenterBasicSubject.next(center);
  }

  cleanCurrentCenter(): void {
    this.currentCIPSubject.next(null);
    this.currentCenterBasicSubject.next(null);
    this.currentCenterAdvancedSubject.next(null);
  }

  all(): Observable<models.CenterBasicInfo[]> {
    return this._http
      .get<models.CenterBasicInfo[]>(environment.apiUrl + '/api-vlight/projects', {
        observe: 'response',
        context: new HttpContext().set(CIP, false),
      })
      .pipe(
        map((httpResponse) => httpResponse.body),
        map((centers) =>
          centers
            .map((center) => {
              center.cip = center.cip.replace('p-', '');
              return center;
            })
            .sort((a, b) => (b.nombreClave > a.nombreClave ? -1 : 1))
        )
      );
  }

  activateCenter(cip: string): Observable<HttpResponseBase> {
    return this._http.patch<HttpResponseBase>(
      environment.apiUrl + '/api-vlight/activate-project',
      {},
      {
        observe: 'response',
        params: new HttpParams().set('CIP', cip),
        context: new HttpContext().set(CIP, false),
      }
    );
  }

  maintenanceCenter(cip: string): Observable<HttpResponseBase> {
    return this._http.get<HttpResponseBase>(environment.apiUrl + '/api-vlight/db/maintenance', {
      observe: 'response',
      params: new HttpParams().set('CIP', cip),
      context: new HttpContext().set(CIP, false),
    });
  }

  reportModeCenter(cip: string, mode: 'ACTIVATE' | 'DEACTIVATE'): Observable<HttpResponseBase> {
    return this._http.patch<HttpResponseBase>(
      environment.apiUrl + '/api-vlight/project/report-mode',
      {},
      {
        observe: 'response',
        params: new HttpParams().set('CIP', cip).set('mode', mode),
        context: new HttpContext().set(CIP, false),
      }
    );
  }

  generateGeneralReportCenter(cip: string): Observable<HttpResponseBase> {
    return this._http.post<HttpResponseBase>(
      environment.apiUrl + '/api-vlight/project/general-report',
      {},
      {
        observe: 'response',
        params: new HttpParams().set('CIP', cip),
        context: new HttpContext().set(CIP, false),
      }
    );
  }

  generateWorkforceReportCenter(cip: string, date?: string): Observable<HttpResponseBase> {
    let queryParams = new HttpParams().set('CIP', cip);

    if (date) {
      queryParams = queryParams.set('date', date);
    }

    return this._http.post<HttpResponseBase>(
      environment.apiUrl + '/api-vlight/project/attendance-report',
      {},
      {
        observe: 'response',
        params: queryParams,
        context: new HttpContext().set(CIP, false),
      }
    );
  }

  selectCenterBasic(cip: string): Observable<any> {
    return this._http
      .get<HttpResponseBase>(environment.apiUrl + '/api-vlight/project', {
        observe: 'response',
        params: new HttpParams().set('CIP', cip),
        context: new HttpContext().set(CIP, false),
      })
      .pipe(map((httpResponse) => httpResponse.body));
  }

  selectCenterAdvanced(cip: string): Observable<models.CenterAdvancedInfo> {
    return this._http
      .get<models.CenterAdvancedInfo>(environment.apiUrl + '/api-vlight/project-advanced', {
        observe: 'response',
        params: new HttpParams().set('CIP', cip),
        context: new HttpContext().set(CIP, false),
      })
      .pipe(
        map((httpResponse) => httpResponse.body),
        tap((center) => {
          this.currentCIP = cip;
          center.fecha = new Date();
          this.currentCenterAdvanced = center;
        })
      );
  }

  cipAvailable(cip: string): Observable<{ status: boolean }> {
    return this._http
      .get<{ status: boolean }>(environment.apiUrl + '/api-vlight/is-cip-available', {
        observe: 'response',
        params: new HttpParams().set('CIP', cip),
        context: new HttpContext().set(CIP, false),
      })
      .pipe(map((httpResponse) => httpResponse.body));
  }

  availableTimeZones(): Observable<{ timezone: string[] }> {
    return this._http
      .get<{ timezone: string[] }>(environment.apiUrl + '/api-vlight/timezones', {
        observe: 'response',
        context: new HttpContext().set(CIP, false),
      })
      .pipe(map((httpResponse) => httpResponse.body));
  }

  createCenter(center: models.NewCenter): Observable<HttpResponseBase> {
    return this._http
      .post<HttpResponseBase>(environment.apiUrl + '/api-vlight/project', center, {
        observe: 'response',
        context: new HttpContext().set(CIP, false),
      })
      .pipe(map((httpResponse) => httpResponse.body));
  }

  getClients(cip?: string): Observable<{ cliente: models.Client[] }> {
    return this._http
      .get<{ cliente: models.Client[] }>(environment.apiUrl + '/api-vlight/clients', {
        observe: 'response',
        context: new HttpContext().set(CIP, cip === undefined ? true : false),
      })
      .pipe(map((httpResponse) => httpResponse.body));
  }

  credentialsHaveBeenPaid(credentials: { worker: string; credential: string }[], cip: string): Observable<models.CredentialsHaveBeenPaid> {
    return this._http
      .patch<models.CredentialsHaveBeenPaid>(environment.apiUrl + '/api-vlight/company/credentials-have-been-paid', {credentials}, {
        observe: 'response',
        params: new HttpParams().set('CIP', cip),
        context: new HttpContext().set(CIP, false),
      })
      .pipe(map((httpResponse) => httpResponse.body));
  }
}
